import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ProjectService } from '@app/services/project.service';
import { ProjectParametersService } from '@app/services/project-parameters.service';
import { ToastrService } from 'ngx-toastr';

function toRowArray(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  return [];
}

function readTemplateCode(t: Record<string, unknown>): string {
  return String(t['MTShortCode'] ?? t['mtShortCode'] ?? '').trim();
}

function readLayoutCode(l: Record<string, unknown>): string {
  return String(l['LayoutShortCode'] ?? l['layoutShortCode'] ?? '').trim();
}

function readCustomerCode(c: Record<string, unknown>): string {
  return String(c['CustomerShortCode'] ?? c['customerShortCode'] ?? '').trim();
}

@Component({
  selector: 'app-project-from-lesson',
  templateUrl: './project-from-lesson.component.html',
  styleUrl: './project-from-lesson.component.scss',
})
export class ProjectFromLessonComponent implements OnInit {
  form!: FormGroup;
  templates: Record<string, unknown>[] = [];
  layouts: Record<string, unknown>[] = [];
  customers: Record<string, unknown>[] = [];
  /** Lessons not yet linked to a project (from backend). */
  availableLessons: Record<string, unknown>[] = [];
  loadingLessons = true;
  loadingTemplates = true;
  loadingCustomers = true;
  saving = false;
  templatesLoadError = '';
  customersLoadError = '';

  constructor(
    private fb: FormBuilder,
    private projectService: ProjectService,
    private projectParamsService: ProjectParametersService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      ProjectShortCode: ['', Validators.required],
      ProjectName: ['', Validators.required],
      CustomerShortCode: ['', Validators.required],
      lessonKey: ['', Validators.required],
      templateId: [''],
      layoutIds: this.fb.control<string[]>([]),
      projectDefaultLayoutSC: [''],
      customFields: this.fb.array([]),
    });

    this.projectParamsService.getTemplates().subscribe({
      next: (data) => {
        this.templates = toRowArray(data);
        this.loadingTemplates = false;
        this.templatesLoadError = this.templates.length ? '' : 'No templates in database (mastertemplate).';
        if (this.templates.length === 1) {
          const code = readTemplateCode(this.templates[0]);
          if (code && !this.form.get('templateId')?.value) {
            this.form.patchValue({ templateId: code });
          }
        }
        this.syncTemplateValidator();
      },
      error: () => {
        this.loadingTemplates = false;
        this.templatesLoadError = 'Could not load templates from server.';
        this.toastr.error(this.templatesLoadError);
        this.syncTemplateValidator();
      },
    });

    this.projectParamsService.getLayouts().subscribe({
      next: (data) => {
        this.layouts = toRowArray(data);
      },
      error: () => this.toastr.warning('Could not load custom layouts'),
    });

    this.projectService.getCustomers().subscribe({
      next: (data) => {
        this.customers = toRowArray(data);
        this.loadingCustomers = false;
        this.customersLoadError = this.customers.length ? '' : 'No customers in database.';
        if (this.customers.length === 1) {
          const code = readCustomerCode(this.customers[0]);
          if (code && !this.form.get('CustomerShortCode')?.value) {
            this.form.patchValue({ CustomerShortCode: code });
          }
        }
        this.syncCustomerValidator();
      },
      error: () => {
        this.loadingCustomers = false;
        this.customersLoadError = 'Could not load customers.';
        this.toastr.error(this.customersLoadError);
        this.syncCustomerValidator();
      },
    });

    this.projectService.lessonsAvailableForPptProject().subscribe({
      next: (rows) => {
        this.availableLessons = toRowArray(rows);
        this.loadingLessons = false;
        if (this.availableLessons.length === 1) {
          const L = this.availableLessons[0];
          const key = `${L['lessonUserId']}:${L['lessonId']}`;
          if (!this.form.get('lessonKey')?.value) {
            this.form.patchValue({ lessonKey: key });
          }
        }
        this.syncLessonValidator();
      },
      error: () => {
        this.loadingLessons = false;
        this.toastr.error('Could not load lessons (run Backend/sql/project_lesson.sql if needed)');
        this.syncLessonValidator();
      },
    });

    this.customFields.push(
      this.fb.group({
        name: ['Logo_img'],
        value: [''],
      })
    );
  }

  private syncTemplateValidator(): void {
    const ctrl = this.form.get('templateId');
    if (!ctrl) return;
    if (this.templates.length > 0) {
      ctrl.setValidators(Validators.required);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private syncCustomerValidator(): void {
    const ctrl = this.form.get('CustomerShortCode');
    if (!ctrl) return;
    ctrl.setValidators(Validators.required);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private syncLessonValidator(): void {
    const ctrl = this.form.get('lessonKey');
    if (!ctrl) return;
    if (this.availableLessons.length > 0) {
      ctrl.setValidators(Validators.required);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  get customFields(): FormArray {
    return this.form.get('customFields') as FormArray;
  }

  get layoutIds(): string[] {
    return (this.form.get('layoutIds')?.value as string[]) || [];
  }

  /** Human-readable reasons the Create button stays disabled. */
  get submitBlockers(): string[] {
    if (!this.form) return ['Form is loading…'];
    const blockers: string[] = [];
    const v = this.form.getRawValue();

    if (!String(v.ProjectShortCode || '').trim()) blockers.push('Project short code');
    if (!String(v.ProjectName || '').trim()) blockers.push('Project name');

    if (this.loadingCustomers) blockers.push('Customers still loading');
    else if (!String(v.CustomerShortCode || '').trim()) blockers.push('Customer');

    if (this.loadingLessons) blockers.push('Lessons still loading');
    else if (this.availableLessons.length && !String(v.lessonKey || '').trim()) {
      blockers.push('Lesson');
    } else if (!this.availableLessons.length) {
      blockers.push('No lessons available to link (all may already be linked)');
    }

    if (this.loadingTemplates) blockers.push('Templates still loading');
    else if (this.templates.length && !String(v.templateId || '').trim()) {
      blockers.push('Template');
    } else if (!this.templates.length) {
      blockers.push('Templates not loaded');
    }

    return blockers;
  }

  canSubmit(): boolean {
    return !this.saving && this.submitBlockers.length === 0 && this.form.valid;
  }

  templateCode(t: Record<string, unknown>): string {
    return readTemplateCode(t);
  }

  layoutCode(l: Record<string, unknown>): string {
    return readLayoutCode(l);
  }

  customerCode(c: Record<string, unknown>): string {
    return readCustomerCode(c);
  }

  templateLabel(t: Record<string, unknown>): string {
    return readTemplateCode(t) || '?';
  }

  layoutLabel(l: Record<string, unknown>): string {
    return readLayoutCode(l) || '?';
  }

  customerLabel(c: Record<string, unknown>): string {
    const code = readCustomerCode(c);
    const name = String(c['CustomerName'] ?? c['customerName'] ?? '').trim();
    return name ? `${code} — ${name}` : code;
  }

  lessonOptionValue(L: Record<string, unknown>): string {
    return `${L['lessonUserId']}:${L['lessonId']}`;
  }

  lessonOptionLabel(L: Record<string, unknown>): string {
    const base = `${L['lessonTitle']} (user ${L['lessonUserId']}, lesson ${L['lessonId']}, v${L['lessonVersion']})`;
    const linked = String(L['linkedProjectCodes'] || '').trim();
    return linked ? `${base} — already linked: ${linked}` : base;
  }

  addCustomField(): void {
    this.customFields.push(
      this.fb.group({
        name: [''],
        value: [''],
      })
    );
  }

  removeCustomField(i: number): void {
    this.customFields.removeAt(i);
  }

  onLayoutChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const code = input.value;
    const layoutIds = [...this.layoutIds];
    if (input.checked) {
      if (!layoutIds.includes(code)) layoutIds.push(code);
    } else {
      const ix = layoutIds.indexOf(code);
      if (ix > -1) layoutIds.splice(ix, 1);
    }
    this.form.get('layoutIds')?.setValue(layoutIds);
    const currentDefault = String(this.form.get('projectDefaultLayoutSC')?.value || '');
    if (currentDefault && !layoutIds.includes(currentDefault)) {
      this.form.get('projectDefaultLayoutSC')?.setValue('');
    }
  }

  isLayoutChecked(code: string): boolean {
    return this.layoutIds.includes(code);
  }

  private parseLessonKey(key: string): { lessonUserId: number; lessonId: number } | null {
    const parts = key.split(':');
    if (parts.length !== 2) return null;
    const lessonUserId = Number(parts[0]);
    const lessonId = Number(parts[1]);
    if (!Number.isFinite(lessonUserId) || !Number.isFinite(lessonId)) return null;
    return { lessonUserId, lessonId };
  }

  save(): void {
    if (!this.canSubmit()) {
      this.form.markAllAsTouched();
      const msg = this.submitBlockers.join(', ');
      this.toastr.warning(msg || 'Complete required fields');
      return;
    }
    const v = this.form.getRawValue();
    const lesson = this.parseLessonKey(v.lessonKey);
    if (!lesson) {
      this.toastr.error('Select a lesson');
      return;
    }
    if (!String(v.templateId || '').trim()) {
      this.toastr.error('Select a template');
      return;
    }
    const customFields = (this.customFields.value as { name: string; value: string }[]).filter(
      (f) => (f.name || '').trim() !== ''
    );
    this.saving = true;
    this.projectService
      .createProjectForLesson({
        ProjectShortCode: v.ProjectShortCode.trim(),
        ProjectName: v.ProjectName.trim(),
        CustomerShortCode: v.CustomerShortCode.trim(),
        lessonUserId: lesson.lessonUserId,
        lessonId: lesson.lessonId,
        templateId: v.templateId,
        layoutIds: v.layoutIds || [],
        projectDefaultLayoutSC: String(v.projectDefaultLayoutSC || '').trim() || null,
        customFields,
      })
      .subscribe({
        next: (res: any) => {
          this.saving = false;
          const msg = res?.message || '';
          if (msg.includes('already exists') || msg.includes('already linked')) {
            this.toastr.warning(msg);
            return;
          }
          this.toastr.success(msg || 'Project created');
          const returnTo = this.router.url.startsWith('/admin')
            ? '/admin/project-management'
            : '/creator/project-list';
          this.router.navigate([returnTo]);
        },
        error: () => {
          this.saving = false;
          this.toastr.error('Save failed');
        },
      });
  }
}
